import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { waApi, type WaChat, type WaSession } from '../../api/whatsapp'
import { DropdownSelect } from '../../components/ui/DropdownSelect'
import { LeadSearchInput } from '../mail/LeadSearchInput'

// LeadSearchInput is not exported with a Lead type — declare the minimal shape it uses.
interface Lead { id: string; firstName: string; lastName: string; email: string }

type Tab = 'lead' | 'number' | 'group'

// Cheap client-side mirror of the backend isPlausiblePhone(): no letters, 8–15 digits.
const phoneDigits = (s: string) => s.replace(/\D/g, '')
const isPlausiblePhone = (s: string) => /^[+\d\s().-]+$/.test(s.trim()) && phoneDigits(s).length >= 8 && phoneDigits(s).length <= 15

export interface NewChatModalProps {
  companyId: string
  sessions: WaSession[]
  onClose: () => void
  onCreated: (chat: WaChat) => void
  /** Manual number that resolved on WhatsApp but isn't linked to a lead yet. */
  onNeedsLeadLink: (chat: WaChat, typedPhone: string) => void
}

export function NewChatModal({ companyId, sessions, onClose, onCreated, onNeedsLeadLink }: NewChatModalProps) {
  const [tab, setTab] = useState<Tab>('lead')
  const [sessionId, setSessionId] = useState(sessions[0]?.id ?? '')
  const [lead, setLead] = useState<Lead | null>(null)
  const [phone, setPhone] = useState('')
  const [subject, setSubject] = useState('')
  const [groupLead, setGroupLead] = useState<Lead | null>(null)
  const [memberIds, setMemberIds] = useState<string[]>([])
  const [error, setError] = useState('')

  const { data: known = [] } = useQuery({
    queryKey: ['wa-known', companyId],
    queryFn: () => waApi.listKnown(companyId),
    enabled: tab === 'group',
  })

  const start = useMutation({
    mutationFn: () =>
      tab === 'group'
        ? waApi.createGroup(companyId, { sessionId, subject, leadId: groupLead!.id, knownContactIds: memberIds })
        : waApi.startChat(companyId, {
            sessionId,
            leadId: tab === 'lead' ? lead!.id : undefined,
            phone: tab === 'number' ? phone : undefined,
          }),
    onSuccess: (chat) => {
      // Manual number that's on WhatsApp but matched no lead → prompt to link/create.
      if (tab === 'number' && !chat.leadId) onNeedsLeadLink(chat, phone)
      else onCreated(chat)
    },
    onError: (e: any) => {
      if (e?.response?.status === 429) {
        setError('Too many attempts — please wait about a minute and try again.')
      } else {
        setError(e?.response?.data?.message ?? 'Failed to start chat')
      }
    },
  })

  const phoneInvalid = tab === 'number' && phone.trim().length > 0 && !isPlausiblePhone(phone)
  const canSubmit =
    !!sessionId &&
    (tab === 'lead'
      ? !!lead
      : tab === 'number'
        ? isPlausiblePhone(phone)
        : !!subject.trim() && !!groupLead)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="w-[28rem] rounded-lg bg-white p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="mb-3 font-semibold">New WhatsApp chat</h3>
        <div className="mb-4 flex gap-1">
          {(['lead', 'number', 'group'] as Tab[]).map(t => (
            <button
              key={t}
              className={`btn btn-sm ${tab === t ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => {
                setTab(t)
                setError('')
              }}
            >
              {t === 'lead' ? 'From lead' : t === 'number' ? 'By number' : 'New group'}
            </button>
          ))}
        </div>

        {sessions.length > 1 && (
          <div className="mb-3">
            <label className="label">Send from</label>
            <DropdownSelect
              value={sessionId}
              onChange={v => setSessionId(v)}
              options={sessions.map(s => ({
                value: s.id,
                label: `${s.name}${s.phoneNumber ? ` (${s.phoneNumber})` : ''}`,
              }))}
            />
          </div>
        )}

        {tab === 'lead' && (
          <div className="mb-3">
            <label className="label">Lead</label>
            <LeadSearchInput companyId={companyId} value={lead} onChange={setLead} required placeholder="Search lead…" />
          </div>
        )}
        {tab === 'number' && (
          <div className="mb-3">
            <label className="label">Phone number</label>
            <input className="input" placeholder="e.g. 491512345678" value={phone} onChange={e => { setPhone(e.target.value); setError('') }} />
            {phoneInvalid ? (
              <p className="mt-1 text-xs text-red-600">Enter the full number with country code, digits only — e.g. 491512345678.</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">Full international number, country code, digits only. You'll link it to a lead next.</p>
            )}
          </div>
        )}
        {tab === 'group' && (
          <div className="mb-3 space-y-3">
            <div>
              <label className="label">Group name</label>
              <input className="input" value={subject} onChange={e => setSubject(e.target.value)} />
            </div>
            <div>
              <label className="label">Lead (required)</label>
              <LeadSearchInput companyId={companyId} value={groupLead} onChange={setGroupLead} required placeholder="Search lead…" />
            </div>
            <div>
              <label className="label">Add known contacts</label>
              {known.filter(k => k.phoneNumber).length === 0 && (
                <p className="text-xs text-gray-500">No known contacts with a WhatsApp number yet — owners can add them in Settings → WhatsApp.</p>
              )}
              <div className="flex flex-wrap gap-1">
                {known.filter(k => k.phoneNumber).map(k => {
                  const on = memberIds.includes(k.id)
                  return (
                    <button
                      key={k.id}
                      className={`btn btn-sm ${on ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => setMemberIds(ids => (on ? ids.filter(i => i !== k.id) : [...ids, k.id]))}
                    >
                      {k.firstName} {k.lastName} <span className="text-xs opacity-70">{k.phoneNumber}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {error && <p className="mb-2 text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={!canSubmit || start.isPending} onClick={() => start.mutate()}>
            {tab === 'group' ? 'Create group' : 'Start chat'}
          </button>
        </div>
      </div>
    </div>
  )
}
